import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { type ElementType, type FC, type ReactNode } from "react";
import { HeadingS } from "../Typography";
import * as Styled from "../ContractTransactions.styled";

export type HomepageTransactionRow = {
    key: string;
    title: string;
    dateLabel: string;
    amountLabel: string;
    icon?: ReactNode;
};

type HomepageTransactionsListProps = {
    heading: string;
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    emptyMessage: string;
    rows: HomepageTransactionRow[];
    showAllTo: string;
    showAllLabel: string;
    linkComponent: ElementType;
};

const RowSkeleton: FC = () => <Skeleton height={45} variant="rectangular" />;

export const HomepageTransactionsList: FC<HomepageTransactionsListProps> = ({
    heading,
    isLoading,
    isError,
    isSuccess,
    emptyMessage,
    rows,
    showAllTo,
    showAllLabel,
    linkComponent,
}) => {
    return (
        <Styled.TransactionsContainer>
            <HeadingS mb={0}>{heading}</HeadingS>

            {isLoading && (
                <Stack spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                        <RowSkeleton key={i} />
                    ))}
                </Stack>
            )}

            {isError && <Styled.TransactionText variant={Styled.TransactionTextVariant.Title}>{emptyMessage}</Styled.TransactionText>}
            {isSuccess && rows.length === 0 && (
                <Styled.TransactionText variant={Styled.TransactionTextVariant.Title}>{emptyMessage}</Styled.TransactionText>
            )}

            {isSuccess && rows.length > 0 && (
                <Stack spacing={4}>
                    {rows.map((row) => (
                        <Styled.TransactionRow key={row.key}>
                            {row.icon && <Styled.TransactionIconWrapper>{row.icon}</Styled.TransactionIconWrapper>}
                            <Styled.TransactionContent>
                                <Styled.TransactionText variant={Styled.TransactionTextVariant.Title}>{row.title}</Styled.TransactionText>
                                <Styled.TransactionText variant={Styled.TransactionTextVariant.Date}>
                                    {row.dateLabel}
                                </Styled.TransactionText>
                            </Styled.TransactionContent>
                            <Styled.TransactionText variant={Styled.TransactionTextVariant.Amount}>
                                {row.amountLabel}
                            </Styled.TransactionText>
                        </Styled.TransactionRow>
                    ))}
                </Stack>
            )}

            <Styled.ShowAllButton
                component={linkComponent}
                to={showAllTo}
                disabled={!isSuccess}
                variant="text"
                size="small"
                endIcon={<ArrowForwardIcon />}
            >
                {showAllLabel}
            </Styled.ShowAllButton>
        </Styled.TransactionsContainer>
    );
};
